import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// web/src/pages/MemoryPage.tsx
// Shadow profile viewer + tabbed agent memory + GDPR controls.
// Shadow profile section is UNCHANGED — only the memory section is modified.
import { useEffect, useState } from "react";
import { AppShell } from "../components/layout/AppShell";
import { ShadowProfilePanel } from "../components/memory/ShadowProfilePanel";
import { MemoryItem } from "../components/memory/MemoryItem";
import { apiRequest } from "../lib/apiClient";
// ── Config ────────────────────────────────────────────────────────────────────
const AGENT_TABS = [
    {
        id: "luna",
        label: "Luna",
        activeColor: "border-violet-500 text-violet-400",
        dotColor: "bg-violet-400",
        description: "Facts, goals, and profile details Luna has collected.",
    },
    {
        id: "argus",
        label: "Argus",
        activeColor: "border-indigo-500 text-indigo-400",
        dotColor: "bg-indigo-400",
        description: "Behavioral dimension snapshots written after each turn.",
    },
    {
        id: "orchestrator",
        label: "Orchestrator",
        activeColor: "border-amber-500 text-amber-400",
        dotColor: "bg-amber-400",
        description: "Reasoning traces from multi-specialist pipeline runs.",
    },
];
// Poll cadence so the page reflects new memory without a manual reload.
const MEMORY_POLL_MS = 10000;
const TIER_TABS = [
    { id: "short", label: "STM", description: "Short-term — active session context" },
    { id: "mid", label: "MTM", description: "Mid-term — patterns from recent sessions" },
    { id: "long", label: "LTM", description: "Long-term — persistent knowledge & facts" },
];
// ── Component ─────────────────────────────────────────────────────────────────
export default function MemoryPage() {
    // Shadow profile state — feeds ShadowProfilePanel unchanged
    const [shadowProfile, setShadowProfile] = useState(null);
    const [shadowLoading, setShadowLoading] = useState(true);
    // Memory state
    const [memories, setMemories] = useState([]);
    const [memoriesLoading, setMemoriesLoading] = useState(false);
    // Tab state
    const [activeAgent, setActiveAgent] = useState("luna");
    const [activeTier, setActiveTier] = useState("long");
    // GDPR
    const [confirmClear, setConfirmClear] = useState(false);
    useEffect(() => {
        let cancelled = false;
        const loadShadow = (showSpinner) => {
            if (showSpinner)
                setShadowLoading(true);
            apiRequest("/profile/shadow")
                .then((data) => {
                if (cancelled)
                    return;
                const cs = data.confidence_scores ?? {};
                const levels = [
                    data.sophistication, data.context_density, data.decision_style,
                    data.risk_behavior, data.temporal_orientation,
                ];
                const hasSignal = levels.some((v) => v !== null) || Object.values(cs).some((v) => v > 0);
                setShadowProfile(hasSignal
                    ? {
                        sophistication: data.sophistication,
                        context_density: data.context_density,
                        decision_style: data.decision_style,
                        risk_behavior: data.risk_behavior,
                        temporal_orientation: data.temporal_orientation,
                        confidence: cs,
                        last_updated: data.last_analyzed_at ?? undefined,
                    }
                    : null);
            })
                .catch(() => { if (!cancelled)
                setShadowProfile(null); })
                .finally(() => { if (!cancelled)
                setShadowLoading(false); });
        };
        const loadMemories = (showSpinner) => {
            if (showSpinner)
                setMemoriesLoading(true);
            apiRequest("/memory/me")
                .then((data) => { if (!cancelled)
                setMemories(data.memories ?? []); })
                .catch(() => { if (!cancelled)
                setMemories([]); })
                .finally(() => { if (!cancelled)
                setMemoriesLoading(false); });
        };
        const loadAll = (showSpinner) => {
            loadShadow(showSpinner);
            loadMemories(showSpinner);
        };
        // Initial load shows spinners; background polls refresh silently so the
        // page reflects new memory as you use the app (no manual reload needed).
        loadAll(true);
        const id = setInterval(() => loadAll(false), MEMORY_POLL_MS);
        const onVisible = () => {
            if (document.visibilityState === "visible")
                loadAll(false);
        };
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            cancelled = true;
            clearInterval(id);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, []);
    function handleDeleteMemory(id) {
        void apiRequest(`/memory/me/${id}`, { method: "DELETE" });
        setMemories((prev) => prev.filter((m) => m.id !== id));
    }
    // When switching agent tab, default to the tier with the most items
    function switchAgent(agentId) {
        setActiveAgent(agentId);
        const agentItems = memories.filter((m) => m.agent_id === agentId);
        const tierWithMost = TIER_TABS.slice().sort((a, b) => agentItems.filter((m) => m.tier === b.id).length -
            agentItems.filter((m) => m.tier === a.id).length)[0];
        if (tierWithMost)
            setActiveTier(tierWithMost.id);
    }
    // Count helpers
    function agentCount(agentId) {
        return memories.filter((m) => m.agent_id === agentId).length;
    }
    function tierCount(agentId, tier) {
        return memories.filter((m) => m.agent_id === agentId && m.tier === tier).length;
    }
    const visibleItems = memories.filter((m) => m.agent_id === activeAgent && m.tier === activeTier);
    const activeAgentCfg = AGENT_TABS.find((a) => a.id === activeAgent);
    const activeTierCfg = TIER_TABS.find((t) => t.id === activeTier);
    return (_jsx(AppShell, { children: _jsx("div", { className: "h-full overflow-y-auto", children: _jsxs("div", { className: "max-w-2xl mx-auto px-6 py-8 space-y-8", children: [_jsxs("div", { children: [_jsx("h1", { className: "text-xl font-bold text-slate-200", children: "Memory & Profile" }), _jsx("p", { className: "text-sm text-slate-500 mt-1", children: "What your agents know and remember about you." })] }), _jsxs("section", { className: "space-y-3", children: [_jsx("h2", { className: "text-xs font-semibold text-slate-500 uppercase tracking-widest", children: "Behavioral Profile" }), _jsx(ShadowProfilePanel, { profile: shadowProfile, isLoading: shadowLoading })] }), _jsxs("section", { className: "space-y-3", children: [_jsx("h2", { className: "text-xs font-semibold text-slate-500 uppercase tracking-widest", children: "Agent Memory" }), _jsxs("div", { className: "bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden", children: [_jsx("div", { className: "flex border-b border-slate-800", children: AGENT_TABS.map((agent) => {
                                            const count = agentCount(agent.id);
                                            const isActive = activeAgent === agent.id;
                                            return (_jsxs("button", { onClick: () => switchAgent(agent.id), className: `
                        flex-1 flex items-center justify-center gap-1.5 px-3 py-3.5
                        text-xs font-medium border-b-2 transition-colors
                        ${isActive
                                                    ? agent.activeColor + " bg-slate-800/40"
                                                    : "border-transparent text-slate-500 hover:text-slate-300 hover:bg-slate-800/20"}
                      `, children: [_jsx("span", { className: `w-1.5 h-1.5 rounded-full shrink-0 ${isActive ? agent.dotColor : "bg-slate-700"}` }), agent.label, _jsx("span", { className: `text-[9px] px-1 py-0.5 rounded ${isActive ? "bg-slate-700 text-slate-300" : "bg-slate-800 text-slate-600"}`, children: count })] }, agent.id));
                                        }) }), _jsxs("div", { className: "flex gap-1 px-4 pt-3 pb-2 border-b border-slate-800/60", children: [TIER_TABS.map((tier) => {
                                                const count = tierCount(activeAgent, tier.id);
                                                const isActive = activeTier === tier.id;
                                                return (_jsxs("button", { onClick: () => setActiveTier(tier.id), className: `
                        flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-colors
                        ${isActive
                                                        ? "bg-slate-800 text-slate-200"
                                                        : "text-slate-600 hover:text-slate-400 hover:bg-slate-800/40"}
                      `, children: [_jsx("span", { className: "font-semibold", children: tier.label }), _jsx("span", { className: `text-[9px] tabular-nums ${isActive ? "text-slate-400" : "text-slate-700"}`, children: count })] }, tier.id));
                                            }), _jsx("span", { className: "ml-auto self-center text-[9px] text-slate-700 italic", children: activeTierCfg.description })] }), _jsx("div", { className: "p-4 space-y-3", children: memoriesLoading ? (_jsx("div", { className: "text-sm text-slate-600 py-4 text-center", children: "Loading\u2026" })) : visibleItems.length === 0 ? (_jsxs("div", { className: "py-6 text-center space-y-1", children: [_jsxs("p", { className: "text-sm text-slate-600", children: ["No ", activeTierCfg.label, " entries for ", activeAgentCfg.label, "."] }), _jsx("p", { className: "text-[10px] text-slate-700", children: activeAgentCfg.description })] })) : (visibleItems.map((item) => (_jsx(MemoryItem, { item: item, onDelete: handleDeleteMemory }, item.id)))) })] })] }), _jsxs("section", { className: "space-y-3", children: [_jsx("h2", { className: "text-xs font-semibold text-slate-500 uppercase tracking-widest", children: "Data controls" }), _jsxs("div", { className: "bg-slate-900 border border-slate-800 rounded-2xl p-5 space-y-4", children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { children: [_jsx("div", { className: "text-sm text-slate-300", children: "Export all data" }), _jsx("div", { className: "text-xs text-slate-600 mt-0.5", children: "Download a copy of all your data (GDPR Article 20)" })] }), _jsx("button", { onClick: () => window.open(`${window.location.origin}/api/profile/export`, "_blank"), className: "px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-slate-200 text-xs rounded-lg transition-colors", children: "Export" })] }), _jsx("div", { className: "border-t border-slate-800" }), _jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { children: [_jsx("div", { className: "text-sm text-rose-400", children: "Delete account" }), _jsx("div", { className: "text-xs text-slate-600 mt-0.5", children: "Permanently erases all data, memories, and conversations" })] }), !confirmClear ? (_jsx("button", { onClick: () => setConfirmClear(true), className: "px-3 py-1.5 bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 text-xs rounded-lg transition-colors", children: "Delete" })) : (_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("button", { onClick: () => void apiRequest("/profile/me", { method: "DELETE" }).then(() => { window.location.href = "/login"; }), className: "px-3 py-1.5 bg-rose-600 hover:bg-rose-500 text-white text-xs rounded-lg transition-colors", children: "Confirm" }), _jsx("button", { onClick: () => setConfirmClear(false), className: "text-xs text-slate-600 hover:text-slate-400 transition-colors", children: "Cancel" })] }))] })] })] })] }) }) }));
}
